import { useCallback } from 'react'
import type { DownloadResult } from '../types'
import { useShareLink } from '../hooks/useShareLink'
import { useArchiveUser } from '../hooks/useArchiveUser'

interface ActionButtonsProps {
  result: DownloadResult | null
  canShare: boolean
  sourceUrl: string | null
  onShareToPhotos: () => Promise<boolean>
}

const SHARE_LINK_LABELS = {
  idle: 'Copy Share Link',
  creating: 'Creating link...',
  copied: 'Link Copied!',
  manual: 'Link Ready',
  error: 'Try Again',
} as const

export function ActionButtons({ result, canShare, sourceUrl, onShareToPhotos }: ActionButtonsProps) {
  const shareLink = useShareLink()
  // A share link is permanent and public, so the server only publishes for a signed-in user.
  // Offering the button to anyone else would just produce a 401 they cannot act on.
  const { user } = useArchiveUser()

  const handleShareClick = useCallback(() => {
    void onShareToPhotos()
  }, [onShareToPhotos])

  const handleShareLinkClick = useCallback(() => {
    if (!sourceUrl) return
    void shareLink.copyShareLink(sourceUrl)
  }, [shareLink, sourceUrl])

  if (!result) return null

  const isCreating = shareLink.state === 'creating'

  return (
    <div className="space-y-3 mt-3">
      {canShare && (
        <button
          onClick={handleShareClick}
          className="w-full py-4.5 text-lg font-semibold bg-success-green text-white rounded-xl transition-colors hover:bg-success-green-dark animate-pulse-subtle"
        >
          Save to Photos
        </button>
      )}

      <a
        href={result.objectUrl}
        download={result.filename}
        className="block w-full py-4.5 text-lg font-semibold bg-ios-blue text-white rounded-xl text-center transition-colors hover:bg-ios-blue-dark"
      >
        Save to Files
      </a>

      {sourceUrl && user && (
        <button
          onClick={handleShareLinkClick}
          disabled={isCreating}
          className="w-full py-4.5 text-lg font-semibold bg-white/15 text-white rounded-xl transition-colors hover:bg-white/25 disabled:opacity-60 disabled:cursor-wait"
        >
          {SHARE_LINK_LABELS[shareLink.state]}
        </button>
      )}

      {shareLink.state === 'manual' && shareLink.shareUrl && (
        <p className="text-white/60 text-[13px] text-center break-all select-all">
          {shareLink.shareUrl}
        </p>
      )}

      {shareLink.state === 'error' && shareLink.errorMessage && (
        <p className="text-red-300 text-[13px] text-center">{shareLink.errorMessage}</p>
      )}

      {canShare && (
        <p className="text-white/60 text-[13px] text-center mt-3">
          Tap "Save to Photos" to open the share sheet and save to your camera roll
        </p>
      )}
    </div>
  )
}
